import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { Headline } from '../../types/Headline';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import HeadlineForm, { HeadlineFormData } from './HeadlineForm';
import { headlineTableConfig } from './HeadlineTableConfig';

const EnhancedHeadlineManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingHeadline, setEditingHeadline] = useState<Headline | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const actions = {
    handleCreate: () => {
      setEditingHeadline(null);
      setShowForm(true);
    },

    handleEdit: (headline: Headline) => {
      setEditingHeadline(headline);
      setShowForm(true);
    },

    handleView: (headline: Headline) => {
      // For now, view is the same as edit
      // In the future, you could implement a read-only view
      setEditingHeadline(headline);
      setShowForm(true);
    }
  };

  const handleFormSubmit = async (formData: HeadlineFormData) => {
    try {
      setIsLoading(true);

      if (editingHeadline) {
        await apiClient.put(`/api/admin/headlines/${editingHeadline.id}`, formData);
      } else {
        await apiClient.post('/api/admin/headlines', formData);
      }

      setShowForm(false);
      setEditingHeadline(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving headline:', error);
      alert('Failed to save headline');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingHeadline(null);
  };

  const handleDelete = async (headlineId: string) => {
    if (!window.confirm('Are you sure you want to delete this headline?')) return;

    try {
      await apiClient.delete(`/api/admin/headlines/${headlineId}`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete headline:', error);
      alert('Failed to delete headline');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Manage Headlines"
          icon={<FileText size={32} />}
          subtitle="Create and manage homepage headlines and announcements"
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <FileText size={20} />
            Add New Headline
          </button>
        </div>

        {/* Enhanced Table */}
        <AdminTable<Headline>
          onView={actions.handleView}
          onEdit={actions.handleEdit}
          onDelete={handleDelete}
          config={headlineTableConfig}
          refreshTrigger={refreshTrigger}
          isLoading={isLoading}
        />

        {/* Form Modal */}
        {showForm && (
          <HeadlineForm
            headline={editingHeadline}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedHeadlineManagement;
